import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { UploadDialog } from "@/components/UploadDialog";
import { MediaLightbox } from "@/components/MediaLightbox";
import { api, fileUrl, videoUrl, posterUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Star, Video, Image as ImageIcon, Upload } from "lucide-react";
import { cn } from "@/lib/utils";

const statusStyles = {
  approved: "bg-green-500/15 text-green-400 border-green-500/30",
  pending: "bg-primary/15 text-primary border-primary/30",
  rejected: "bg-destructive/15 text-red-400 border-destructive/30",
};

const MyUploads = () => {
  const { user, loading: authLoading, requireAction, requireUpload } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/my/media");
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    load();
  }, [user, authLoading, load, navigate]);

  if (authLoading || !user) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen relative z-10">
      <Navbar onUpload={() => requireUpload() && setUploadOpen(true)} />
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-heading text-4xl font-light tracking-tighter">My Uploads</h1>
        <p className="text-muted-foreground text-sm mt-1">Track your submissions and their status.</p>

        {loading ? (
          <div className="h-64 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <div className="mt-10 border border-dashed border-border rounded-lg py-16 text-center">
            <p className="text-muted-foreground">You haven't submitted anything yet.</p>
            <button
              data-testid="myuploads-submit-btn"
              onClick={() => setUploadOpen(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 font-medium hover:-translate-y-0.5 transition-transform"
            >
              <Upload className="w-4 h-4" /> Submit footage
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-8">
            {items.map((item) => (
              <div
                key={item.id}
                data-testid={`my-item-${item.id}`}
                onClick={() => item.status === "approved" && setSelected(item.id)}
                className={cn("rounded-lg border border-border bg-card overflow-hidden", item.status === "approved" && "cursor-pointer hover:border-primary/40 transition-colors")}
              >
                <div className="relative aspect-video bg-black overflow-hidden">
                  {item.media_type === "video" ? (
                    <video src={videoUrl(item)} poster={posterUrl(item, 600)} muted className="w-full h-full object-cover" />
                  ) : (
                    <img src={fileUrl(item.file_ref)} alt={item.title} className="w-full h-full object-cover" />
                  )}
                  <span className="absolute top-2 left-2 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-black/70 flex items-center gap-1">
                    {item.media_type === "video" ? <Video className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                  </span>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-medium text-sm line-clamp-1">{item.title}</h3>
                    <span className={cn("shrink-0 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border capitalize", statusStyles[item.status])}>
                      {item.status}
                    </span>
                  </div>
                  {item.rating_count > 0 && (
                    <div className="flex items-center gap-1 mt-2 text-sm">
                      <Star className="w-4 h-4 fill-primary text-primary" />
                      <span className="font-mono font-semibold">{item.avg_rating}</span>
                      <span className="text-muted-foreground text-xs">· {item.rating_count} votes</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={load} />
      <MediaLightbox mediaId={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={load} />
    </div>
  );
};

export default MyUploads;
